import { useEffect, useMemo, useRef, useState } from 'react';
import {
  validerConsultation, REGISTRES, REGISTRE_PAR_DEFAUT, estRegistre, prochainNumero, controlerRegistre, libelleRegistre, fusionnerPatients,
} from '../../../shared/business-logic/index.ts';
import type { Registre, Patient, PatientServeur, ElementFile } from '../../../shared/business-logic/index.ts';
import type { ConsultationInput } from '../../../shared/business-logic/validation.ts';
import {
  DIAGNOSTICS_REF, DIAGNOSTICS_COURANTS, DIAGNOSTICS_MASQUES, DIAGNOSTICS_PALUDISME, RULES,
} from '../../../shared/business-logic/constants.ts';
import { getSession } from '../api/client.ts';
import { controlerDateSaisie, ajouterJours } from '../../../shared/business-logic/semaines.ts';
import {
  enregistrerConsultation, enregistrerModification, rafraichirCacheConsultations, supprimerPatient, supprimerToutesConsultations, modifierPatientLocal, ecouterEtatSynchro,
} from '../offline/syncQueue.ts';
import { chargerConsultationsCache, listerParPeriode, purgerConsultationsPeriode, enregistrerTotauxLocaux } from '../offline/db.ts';
import { ConfirmDialog, Pastille } from '../components/ui.tsx';
import ImportConsultations from '../components/ImportConsultations.tsx';
import SaisieVocale from '../components/SaisieVocale.tsx';
import { AFFICHAGE } from '../config/affichage.ts';
import { TotauxRceEnDirect } from '../components/TotauxEnDirect.tsx';
import { calculerRmaLocal, totauxLocaux, patientsComptes } from '../../../shared/business-logic/calculLocal.ts';
import type { Periode } from './Accueil.tsx';

const TDR_OPTIONS = [
  { value: '100', label: '100 — négatif' },
  { value: '101', label: '101 — positif' },
  { value: '110', label: '110 — positif' },
  { value: '111', label: '111 — positif' },
];
const TRAITEMENTS = ['ACT', 'Artesunate injectable', 'Autre', 'ACT, Artesunate injectable'];
const TRAUMATISMES = [
  { code: 'VIOLENCE_SEXUELLE', libelle: 'Violence sexuelle' },
  { code: 'AUTRE_VIOLENCE', libelle: 'Autre cas violence' },
  { code: 'AUTRE_TRAUMATISME', libelle: 'Autre cas traumatisme' },
];
const ADMISSIONS_CRENAS = RULES.crenas.admissions;

const initial: ConsultationInput = {
  numeroRceBrut: '',
  casFievre: null,
  tdrResultat: null,
  ageBrut: '',
  genre: null,
  diagnostics: [],
  typeTraumatisme: null,
  traitementPalu: null,
  indicateurCrenas: null,
  admissionCrenas: null,
  dateConsultation: '',
};

/** Date du jour (calendrier local de l'appareil, AAAA-MM-JJ). */
const aujourdhui = (): string => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`; };
const cleDate = (periodeId: string) => `prerma_derniere_date_${periodeId}`;
/** Date proposée : la dernière date saisie pour ce mois (saisie en série), sinon aujourd'hui si c'est dans le mois — sinon vide (l'agent choisit). */
function dateParDefaut(p: { id: string; annee: number; mois: number }): string {
  const memo = localStorage.getItem(cleDate(p.id));
  if (memo && controlerDateSaisie(memo, p, aujourdhui()) === null) return memo;
  const t = aujourdhui();
  return controlerDateSaisie(t, p, t) === null ? t : '';
}


const CLE_REGISTRE = 'prerma_registre';
const CLE_SESSION = 'prerma_session_debut';
/** Début de la « session » = ouverture de l'application dans cet onglet (conservé en cas de rechargement, remis à zéro à la fermeture). */
function debutSession(): number {
  const v = Number(sessionStorage.getItem(CLE_SESSION));
  if (Number.isFinite(v) && v > 0) return v;
  const now = Date.now();
  sessionStorage.setItem(CLE_SESSION, String(now));
  return now;
}
const libelleDiag = (code: string) => DIAGNOSTICS_REF.find((d) => d.code === code)?.libelle ?? code;

function ChipDiag({ code, libelle, ordre, onToggle }: { code: string; libelle: string; ordre: number; onToggle: (c: string) => void }) {
  const selected = ordre >= 0;
  return (
    <div role="button" tabIndex={0} aria-pressed={selected} className={'chip' + (selected ? ' selected' : '')} onClick={() => onToggle(code)}
      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(code); } }}>
      {selected && <span className="chip-order">{ordre + 1}</span>}
      {libelle}
    </div>
  );
}

export default function SaisieUnique({ periode }: { periode: Periode | null }) {
  const [form, setForm] = useState<ConsultationInput>(initial);
  const [envoi, setEnvoi] = useState<{ ok: boolean; message: string } | null>(null);
  const [enCours, setEnCours] = useState(false);
  const [serveur, setServeur] = useState<PatientServeur[]>([]);
  const [file, setFile] = useState<ElementFile[]>([]);
  const [edition, setEdition] = useState<{ id?: string; localId?: string; version?: number } | null>(null);
  const [registre, setRegistre] = useState<Registre>(() => { const r = localStorage.getItem(CLE_REGISTRE); return estRegistre(r) ? r : REGISTRE_PAR_DEFAUT; });
  const [portee, setPortee] = useState<'session' | 'mois'>('session');
  const [masquesOuverts, setMasquesOuverts] = useState(false);
  const [voixOuverte, setVoixOuverte] = useState(false); // panneau « Saisie vocale » (dictée ligne par ligne)
  const [rechercheDiag, setRechercheDiag] = useState('');
  const [aSupprimer, setASupprimer] = useState<Patient | null>(null);
  const [suppressionEnCours, setSuppressionEnCours] = useState(false);
  const [toutSupprimer, setToutSupprimer] = useState(false);
  const [toutSuppressionEnCours, setToutSuppressionEnCours] = useState(false);
  const [debut] = useState(debutSession);
  const [listeChargee, setListeChargee] = useState(false); // les totaux ne sont rangés dans IndexedDB qu'une fois la liste réellement chargée (jamais « 0 » par erreur)
  const rceInputRef = useRef<HTMLInputElement>(null);
  const chargementId = useRef(0); // ignore les réponses de rechargement périmées
  const suppressionToutRef = useRef(false); // pas de rechargement automatique pendant « Supprimer tout »

  useEffect(() => {
    setListeChargee(false);
    if (periode) void chargerListe(periode.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode?.id]);
  // La liste se met à jour toute seule quand la synchronisation avance (envoi terminé, suppression confirmée…).
  useEffect(() => ecouterEtatSynchro((etat) => {
    if (periode && etat === 'IDLE' && !suppressionToutRef.current) void chargerListe(periode.id);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [periode?.id]);

  async function chargerListe(periodeId: string) {
    const id = ++chargementId.current;
    let rows: unknown[];
    try { rows = await rafraichirCacheConsultations(periodeId); } catch { rows = await chargerConsultationsCache(periodeId); }
    const enFile = (await listerParPeriode(periodeId)) as unknown as ElementFile[];
    if (id !== chargementId.current) return; // une version plus récente est en cours : on ignore celle-ci
    // Patients dont la suppression est en file : on les masque même si le serveur les renvoie encore.
    const enSuppression = new Set(
      (enFile as any[]).filter((i) => i.operation === 'DELETE' && i.syncStatus !== 'SYNCED').map((i) => i.consultationId),
    );
    // « Supprimer tout » en attente d'envoi : tout ce que le serveur renvoie encore pour ce mois est déjà supprimé pour l'utilisateur.
    const toutEnSuppression = (enFile as any[]).some((i) => i.operation === 'DELETE_ALL' && i.syncStatus === 'PENDING');
    setServeur(toutEnSuppression ? [] : (rows as any[]).filter((r) => !enSuppression.has(r.id)) as unknown as PatientServeur[]);
    setFile(enFile);
    setListeChargee(true);
  }

  function choisirRegistre(r: Registre) { setRegistre(r); localStorage.setItem(CLE_REGISTRE, r); }

  function ouvrirPourEdition(p: Patient) {
    setEdition({ id: p.id, localId: p.localId, version: p.version });
    setForm(p.data);
    setEnvoi(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }

  function annulerEdition() {
    setEdition(null);
    setForm({ ...initial, dateConsultation: periode ? dateParDefaut(periode) : '' });
    setEnvoi(null);
  }

  const validation = useMemo(() => validerConsultation(form), [form]);
  // Contrôle « bon registre » (bloquant) : un patient de 5 ans et plus ne se saisit pas dans le registre < 5 ans, et inversement.
  // Date de consultation : proposée à l'ouverture du mois (modifiable), jamais devinée si le mois n'est pas le mois courant.
  useEffect(() => {
    if (periode && !edition) setForm((f) => (f.dateConsultation ? f : { ...f, dateConsultation: dateParDefaut(periode) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [periode?.id]);
  const erreurDate = useMemo(
    () => (periode ? controlerDateSaisie(form.dateConsultation, periode, aujourdhui()) : 'Aucune période.'),
    [form.dateConsultation, periode],
  );
  const erreurRegistre = useMemo(() => controlerRegistre(registre, form.ageBrut), [registre, form.ageBrut]);

  // Patients = état serveur (ou cache) + file d'attente locale : inclut donc les patients saisis hors connexion.
  const { patients, session } = useMemo(() => fusionnerPatients(serveur, file, debut), [serveur, file, debut]);
  const patientsAffiches = useMemo(() => [...(portee === 'session' ? session : patients)].reverse(), [portee, session, patients]); // dernier saisi en haut
  // TOTAUX LOCAUX : recalculés dès que la liste change (nouvelle saisie, import, modification, suppression, retour du serveur).
  // Même moteur que le serveur → mêmes chiffres hors connexion qu'en ligne ; instantané, aucun appel réseau.
  const comptes = useMemo(() => patientsComptes(patients), [patients]);
  const totaux = useMemo(() => totauxLocaux(calculerRmaLocal(comptes.map((p) => p.data))), [comptes]);
  const nbRefuses = patients.length - comptes.length;
  useEffect(() => {
    if (!periode || !listeChargee) return;
    enregistrerTotauxLocaux(periode.id, getSession().csbId ?? '', totaux as unknown as Record<string, unknown>).catch(() => { /* stockage indisponible : l'écran reste exact, seul le report hors connexion manque */ });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totaux, periode?.id, listeChargee]);
  const numeroSuivant = useMemo(() => prochainNumero(patients.map((p) => p.data), registre), [patients, registre]);

  const diagsRemplis = form.diagnostics;
  const estCrenas = form.numeroRceBrut.trim().toUpperCase() === 'CRENAS';
  const hasPalu = diagsRemplis.some((d) => (DIAGNOSTICS_PALUDISME as readonly string[]).includes(d));
  const hasMas = diagsRemplis.includes('MAS');
  const hasMam = diagsRemplis.includes('MAM');
  const showCrenasSection = hasMas || hasMam || estCrenas;

  // Diagnostics : principaux affichés par défaut ; les autres derrière « + ». Un diagnostic masqué déjà choisi reste toujours visible.
  const termeDiag = rechercheDiag.trim().toLowerCase();
  const masquesFiltres = DIAGNOSTICS_MASQUES.filter((d) => !termeDiag || d.libelle.toLowerCase().includes(termeDiag) || d.code.toLowerCase().includes(termeDiag));
  const masquesChoisis = DIAGNOSTICS_MASQUES.filter((d) => form.diagnostics.includes(d.code));
  const anciensChoisis = DIAGNOSTICS_REF.filter((d) => d.legacy && form.diagnostics.includes(d.code)); // ancien code (consultation déjà saisie)

  function toggleDiag(code: string) {
    const next = [...form.diagnostics];
    const idx = next.indexOf(code);
    if (idx >= 0) {
      next.splice(idx, 1);
    } else {
      if (next.length >= 4) return;
      next.push(code);
    }
    setForm({ ...form, diagnostics: next });
  }

  function toggleIndicateurCrenas(valeur: string) {
    const courants = form.indicateurCrenas ? form.indicateurCrenas.split(', ').filter(Boolean) : [];
    const next = courants.includes(valeur) ? courants.filter((v) => v !== valeur) : [...courants, valeur];
    setForm({ ...form, indicateurCrenas: next.length ? next.join(', ') : null });
  }

  async function enregistrer() {
    if (!validation.ok || erreurRegistre || erreurDate || !periode) return;
    setEnCours(true);
    setEnvoi(null);
    try {
      if (edition?.id) {
        await enregistrerModification(periode.id, getSession().csbId ?? '', edition.id, edition.version ?? 1, form);
        setEnvoi({ ok: true, message: 'Modification enregistrée sur cet appareil.' });
      } else if (edition?.localId) {
        await modifierPatientLocal(edition.localId, periode.id, getSession().csbId ?? '', form);
        setEnvoi({ ok: true, message: 'Modification enregistrée sur cet appareil.' });
      } else {
        await enregistrerConsultation(periode.id, getSession().csbId ?? '', form);
        setEnvoi({ ok: true, message: 'Consultation enregistrée sur cet appareil.' });
      }
      localStorage.setItem(cleDate(periode.id), form.dateConsultation ?? '');   // saisie en série : la même date reste proposée pour le patient suivant
      setForm({ ...initial, dateConsultation: form.dateConsultation ?? '' });
      setEdition(null);
      await chargerListe(periode.id);
      rceInputRef.current?.focus(); // gain de temps : prêt à saisir le patient suivant immédiatement
    } catch (err: any) {
      setEnvoi({ ok: false, message: err.message ?? "Erreur d'enregistrement local" });
    } finally {
      setEnCours(false);
    }
  }

  async function confirmerSuppression() {
    if (!aSupprimer || !periode) return;
    setSuppressionEnCours(true);
    try {
      const p = aSupprimer;
      await supprimerPatient({ id: p.id, localId: p.localId, version: p.version, data: p.data }, periode.id, getSession().csbId ?? '');
      if (edition && ((edition.id && edition.id === p.id) || (edition.localId && edition.localId === p.localId))) annulerEdition();
      setEnvoi({ ok: true, message: `Patient N° ${p.data.numeroRceBrut || '—'} supprimé.` });
      setASupprimer(null);
      await chargerListe(periode.id);
    } catch (err: any) {
      setEnvoi({ ok: false, message: err.message ?? 'Suppression impossible.' });
      setASupprimer(null);
    } finally {
      setSuppressionEnCours(false);
    }
  }

  // Suppression de tous les patients de l'onglet affiché (« Cette session » ou « Tout le mois »)
  async function confirmerSuppressionTous() {
    if (!periode) return;
    setToutSuppressionEnCours(true);
    suppressionToutRef.current = true;
    const cibles = [...patientsAffiches];
    const csbId = getSession().csbId ?? '';
    let supprimes = 0;
    let echecs = 0;
    try {
      if (portee === 'mois') {
        // « Tout le mois » : purge COMPLÈTE des consultations du mois — état local, file IndexedDB (y compris saisies bloquées en
        // attente / erreur / conflit) et serveur. Ne concerne pas le CPN ni les autres modules.
        // La purge LOCALE est prioritaire et ne dépend pas du réseau : si l'ordre serveur échoue (session invalide/expirée 401/403, serveur
        // injoignable, erreur inattendue), on FORCE la purge locale et on libère l'écran immédiatement ; l'ordre repartira plus tard.
        let r: { serveur: 'FAITE' | 'PLANIFIEE' };
        try { r = await supprimerToutesConsultations(periode.id, csbId); }
        catch { await purgerConsultationsPeriode(periode.id, true); r = { serveur: 'PLANIFIEE' }; }
        setEdition(null); setForm(initial);
        setEnvoi({
          ok: true,
          message: r.serveur === 'FAITE'
            ? 'Toutes les consultations du mois ont été supprimées.'
            : 'Consultations supprimées sur cet appareil. La suppression sera envoyée au serveur dès le retour de la connexion.',
        });
        setToutSupprimer(false);
        await chargerListe(periode.id);
        return;
      }
      // Séquentiel : évite les conflits dans la file de synchronisation locale.
      for (const p of cibles) {
        try {
          await supprimerPatient({ id: p.id, localId: p.localId, version: p.version, data: p.data }, periode.id, csbId);
          supprimes++;
        } catch {
          echecs++;
        }
      }
      if (edition && cibles.some((p) => (edition.id && edition.id === p.id) || (edition.localId && edition.localId === p.localId))) annulerEdition();
      setEnvoi({
        ok: echecs === 0,
        message: echecs === 0
          ? `${supprimes} patient(s) supprimé(s).`
          : `${supprimes} patient(s) supprimé(s), ${echecs} échec(s). Réessayez pour les restants.`,
      });
      setToutSupprimer(false);
      await chargerListe(periode.id);
    } catch (err: any) {
      setEnvoi({ ok: false, message: err.message ?? 'Suppression impossible.' });
      setToutSupprimer(false);
    } finally {
      suppressionToutRef.current = false;
      setToutSuppressionEnCours(false);
    }
  }

  if (!periode) {
    return <p>Sélectionnez d'abord une période sur l'écran Accueil.</p>;
  }
  if (periode.statut === 'CLOTURE') {
    return <p>Cette période est clôturée : lecture seule. Contactez l'administrateur pour la rouvrir si nécessaire.</p>;
  }

  return (
    <div>
      <fieldset className="registre-selecteur" disabled={!!edition}>
        <legend className="titre">Registre</legend>
        <div className="segments" role="radiogroup" aria-label="Type de registre">
          {REGISTRES.map((r) => (
            <label key={r.id} className={'segment' + (registre === r.id ? ' on' : '')}>
              <input type="radio" name="registre" value={r.id} checked={registre === r.id} onChange={() => choisirRegistre(r.id)} />
              <b>{r.libelle}</b><small>{r.aide}</small>
            </label>
          ))}
        </div>
        <div className="registre-info">
          <span>Prochain N° — {libelleRegistre(registre)} : <b className="numero">{numeroSuivant}</b></span>
          {!edition && <button type="button" className="secondaire" onClick={() => setForm({ ...form, numeroRceBrut: String(numeroSuivant) })}>Utiliser ce N°</button>}
        </div>
      </fieldset>

      <h1>{edition ? 'Modifier une consultation' : 'Saisie RCE/Reg CRENAS'}</h1>
      {edition && (
        <p>
          Modification de la consultation N° RCE <strong>{form.numeroRceBrut}</strong>.{' '}
          <button type="button" className="secondaire" onClick={annulerEdition}>Annuler</button>
        </p>
      )}

      {!edition && (
        <div className="counter-bar">
          <span>Patients saisis ce mois-ci</span>
          <b>{patients.length}</b>
        </div>
      )}

      {!edition && listeChargee && <TotauxRceEnDirect totaux={totaux} refuses={nbRefuses} />}

      {!edition && (
        <>
          <button type="button" className="vocal-ouvrir" aria-expanded={voixOuverte} onClick={() => setVoixOuverte(!voixOuverte)}>
            {voixOuverte ? '✖ Fermer la saisie vocale' : '🎙️ Saisie vocale'}
          </button>
          {voixOuverte && (
            <SaisieVocale
              periode={periode}
              csbId={getSession().csbId ?? ''}
              registre={registre}
              existants={patients.map((p) => ({ numeroRceBrut: p.data.numeroRceBrut, ageBrut: p.data.ageBrut }))}
              dateParDefaut={dateParDefaut(periode)}
              aujourdhui={aujourdhui()}
              onEnregistre={() => chargerListe(periode.id)}
            />
          )}
        </>
      )}

      {!edition && AFFICHAGE.importConsultations && (
        <ImportConsultations
          periodeId={periode.id}
          csbId={getSession().csbId ?? ''}
          existants={patients.map((p) => ({ numeroRceBrut: p.data.numeroRceBrut, ageBrut: p.data.ageBrut }))}
          onImporte={() => chargerListe(periode.id)}
        />
      )}

      <label htmlFor="dateConsultation">Date de consultation *</label>
      <input id="dateConsultation" type="date" value={form.dateConsultation ?? ''} required
        min={periode ? `${periode.annee}-${String(periode.mois).padStart(2, '0')}-01` : undefined}
        max={periode ? (() => { const fin = `${periode.annee}-${String(periode.mois).padStart(2, '0')}-${String(new Date(periode.annee, periode.mois, 0).getDate()).padStart(2, '0')}`; const t = ajouterJours(aujourdhui(), 0); return t < fin ? t : fin; })() : undefined}
        onChange={(e) => setForm({ ...form, dateConsultation: e.target.value })} />
      {erreurDate && (form.dateConsultation !== '' || !!edition) && <p className="erreur" role="alert">{erreurDate}</p>}
      {!form.dateConsultation && <p className="aide">Indiquez le jour de la consultation : il sert aux rapports hebdomadaires (RSH, MNT/MTN, DHIS2).</p>}

      <label htmlFor="rce">N° RCE *</label>
      <input id="rce" ref={rceInputRef} value={form.numeroRceBrut} placeholder='ex: 12  ou  12, 13  ou  CRENAS'
        onChange={(e) => setForm({ ...form, numeroRceBrut: e.target.value })} />

      <label>Cas de fièvre</label>
      <div className="chip-grid">
        {['Oui', 'Non'].map((v) => (
          <div key={v} className={'chip' + (form.casFievre === v ? ' selected' : '')}
            onClick={() => setForm({ ...form, casFievre: (form.casFievre === v ? null : v) as any, tdrResultat: v === 'Non' ? null : form.tdrResultat })}>
            {v}
          </div>
        ))}
      </div>

      {form.casFievre === 'Oui' && (
        <>
          <label>Résultat TDR *</label>
          <div className="chip-grid">
            {TDR_OPTIONS.map((o) => (
              <div key={o.value} className={'chip' + (form.tdrResultat === o.value ? ' selected' : '')}
                onClick={() => setForm({ ...form, tdrResultat: (form.tdrResultat === o.value ? null : o.value) as any })}>
                {o.label}
              </div>
            ))}
          </div>
        </>
      )}

      <label htmlFor="age">Âge * (ex: "15 jours", "12 mois", "10 ans")</label>
      <input id="age" value={form.ageBrut ?? ''} onChange={(e) => setForm({ ...form, ageBrut: e.target.value })} />

      <label>Genre *</label>
      <div className="chip-grid">
        {['M', 'F'].map((v) => (
          <div key={v} className={'chip' + (form.genre === v ? ' selected' : '')}
            onClick={() => setForm({ ...form, genre: (form.genre === v ? null : v) as any })}>
            {v}
          </div>
        ))}
      </div>

      <label>Diagnostics / Symptômes (jusqu'à 4, dans l'ordre de sélection)</label>
      <div className="chip-grid">
        {[...DIAGNOSTICS_COURANTS, ...anciensChoisis].map((d) => (
          <ChipDiag key={d.code} code={d.code} libelle={d.libelle} ordre={form.diagnostics.indexOf(d.code)} onToggle={toggleDiag} />
        ))}
        {/* Diagnostics masqués déjà choisis : toujours visibles (panneau fermé) pour ne jamais « perdre » une sélection de vue. */}
        {!masquesOuverts && masquesChoisis.map((d) => (
          <ChipDiag key={d.code} code={d.code} libelle={d.libelle} ordre={form.diagnostics.indexOf(d.code)} onToggle={toggleDiag} />
        ))}
      </div>
      <div className="diag-outils">
        <button type="button" className="diag-plus" aria-expanded={masquesOuverts} aria-controls="diag-masques" onClick={() => setMasquesOuverts(!masquesOuverts)}>
          {masquesOuverts ? '− Masquer les autres diagnostics' : `+ Autres diagnostics (${DIAGNOSTICS_MASQUES.length})`}
        </button>
      </div>
      {masquesOuverts && (
        <div id="diag-masques" className="diag-masques">
          <input type="search" aria-label="Rechercher un diagnostic" placeholder="Rechercher un diagnostic…" value={rechercheDiag} onChange={(e) => setRechercheDiag(e.target.value)} />
          <div className="chip-grid">
            {masquesFiltres.map((d) => <ChipDiag key={d.code} code={d.code} libelle={d.libelle} ordre={form.diagnostics.indexOf(d.code)} onToggle={toggleDiag} />)}
            {masquesFiltres.length === 0 && <span className="note">Aucun diagnostic ne correspond à « {rechercheDiag} ».</span>}
          </div>
        </div>
      )}
      <div className="chip-count">{form.diagnostics.length}/4 sélectionné(s)</div>

      <label>Type de traumatisme</label>
      <select value={form.typeTraumatisme ?? ''} onChange={(e) => setForm({ ...form, typeTraumatisme: e.target.value || null })}>
        <option value="">Aucun</option>
        {TRAUMATISMES.map((t) => <option key={t.code} value={t.code}>{t.libelle}</option>)}
      </select>

      {hasPalu && (
        <>
          <label>Traitement paludisme *</label>
          <div className="chip-grid">
            {TRAITEMENTS.map((t) => (
              <div key={t} className={'chip' + (form.traitementPalu === t ? ' selected' : '')}
                onClick={() => setForm({ ...form, traitementPalu: form.traitementPalu === t ? null : t })}>
                {t}
              </div>
            ))}
          </div>
        </>
      )}

      {showCrenasSection && (
        <>
          {(hasMas || hasMam) && (
            <div>
              <label>Indicateur CRENAS (dépistage)</label>
              <div className="chip-grid">
                {[...(hasMas ? RULES.crenas.indicateursMas : []), ...(hasMam ? RULES.crenas.indicateursMam : [])].map((v) => {
                  const actif = (form.indicateurCrenas ?? '').includes(v);
                  return (
                    <div key={v} className={'chip' + (actif ? ' selected' : '')} onClick={() => toggleIndicateurCrenas(v)}>
                      {v}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          <label>Admission CRENAS {estCrenas ? '*' : ''}</label>
          <div className="chip-grid">
            {ADMISSIONS_CRENAS.map((a) => (
              <div key={a} className={'chip' + (form.admissionCrenas === a ? ' selected' : '')}
                onClick={() => setForm({ ...form, admissionCrenas: form.admissionCrenas === a ? null : a })}>
                {a}
              </div>
            ))}
          </div>
        </>
      )}

      {(!validation.ok || erreurRegistre) && (
        <div className="messages-erreur">
          <strong>Contrôle de saisie :</strong>
          <ul>
            {validation.erreurs.map((e, i) => <li key={i}>{e}</li>)}
            {erreurRegistre && <li>{erreurRegistre}</li>}
          </ul>
        </div>
      )}
      {validation.ok && !erreurRegistre && form.numeroRceBrut && <p style={{ color: '#0369a1' }}>✅ Saisie valide</p>}

      <div style={{ marginTop: 20 }}>
        <button onClick={enregistrer} disabled={!validation.ok || !!erreurRegistre || !!erreurDate || !form.numeroRceBrut || enCours}>
          {enCours ? 'Enregistrement…' : edition ? 'Enregistrer la modification' : 'Enregistrer et continuer'}
        </button>
      </div>
      {envoi && <p className={envoi.ok ? '' : 'messages-erreur'}>{envoi.message}</p>}

      <section className="patients-liste" style={{ marginTop: 32 }} aria-label="Patients saisis">
        <div className="entete">
          <h2>Patients saisis</h2>
          <div className="onglets" role="tablist" style={{ padding: 0, margin: 0 }}>
            <button role="tab" aria-selected={portee === 'session'} className={'onglet' + (portee === 'session' ? ' actif' : '')} onClick={() => setPortee('session')}>Cette session <span className="compteur">{session.length}</span></button>
            <button role="tab" aria-selected={portee === 'mois'} className={'onglet' + (portee === 'mois' ? ' actif' : '')} onClick={() => setPortee('mois')}>Tout le mois <span className="compteur">{patients.length}</span></button>
          </div>
          {(patientsAffiches.length > 0 || (portee === 'mois' && file.length > 0)) && (
            <button type="button" className="suppr" onClick={() => setToutSupprimer(true)}>
              🗑 Supprimer tout ({patientsAffiches.length})
            </button>
          )}
        </div>
        {patientsAffiches.length === 0 ? (
          <p className="note">{portee === 'session' ? 'Aucun patient saisi pendant cette session. « Tout le mois » affiche aussi les patients saisis avant.' : 'Aucun patient saisi ce mois-ci.'}</p>
        ) : (
          <div className="defil">
            <table>
              <thead><tr><th>N° RCE</th><th>Âge</th><th>Genre</th><th>Diagnostics</th><th>État</th><th /></tr></thead>
              <tbody>
                {patientsAffiches.map((p) => (
                  <tr key={p.cle} className={p.statut === 'ERREUR' || p.statut === 'CONFLIT' ? 'erreur' : p.enAttente ? 'attente' : ''}>
                    <td>{p.data.numeroRceBrut}</td>
                    <td>{p.data.ageBrut}</td>
                    <td>{p.data.genre}</td>
                    <td>{p.data.diagnostics.map(libelleDiag).join(' · ')}</td>
                    <td>
                      {p.statut === 'ATTENTE' && <Pastille niveau="warn">En attente d'envoi</Pastille>}
                      {p.statut === 'ERREUR' && <Pastille niveau="bad" >Refusé</Pastille>}
                      {p.statut === 'CONFLIT' && <Pastille niveau="bad">Conflit</Pastille>}
                      {!p.statut && <Pastille niveau="ok">Enregistré</Pastille>}
                      {p.message && <small className="note" style={{ display: 'block' }}>{p.message}</small>}
                    </td>
                    <td className="actions">
                      <button type="button" className="secondaire" onClick={() => ouvrirPourEdition(p)}>Modifier</button>
                      <button type="button" className="suppr" aria-label={`Supprimer le patient N° ${p.data.numeroRceBrut}`} onClick={() => setASupprimer(p)}>🗑 Supprimer</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {aSupprimer && (
        <ConfirmDialog titre="Êtes-vous sûr de vouloir supprimer ce patient ?" libelleConfirmer="Oui, supprimer" enCours={suppressionEnCours}
          onConfirmer={confirmerSuppression} onAnnuler={() => setASupprimer(null)}>
          <dl>
            <dt>N° RCE</dt><dd>{aSupprimer.data.numeroRceBrut || '—'}</dd>
            <dt>Âge</dt><dd>{aSupprimer.data.ageBrut || '—'}</dd>
            <dt>Genre</dt><dd>{aSupprimer.data.genre || '—'}</dd>
            <dt>Diagnostics</dt><dd>{aSupprimer.data.diagnostics.map(libelleDiag).join(', ') || '—'}</dd>
          </dl>
          <p><strong>Cette action est définitive.</strong> Le patient sera retiré de la saisie, du RMA et des exports.{aSupprimer.id ? '' : " Ce patient n'a pas encore été envoyé au serveur."}</p>
        </ConfirmDialog>
      )}

      {toutSupprimer && (
        <ConfirmDialog
          titre={`Supprimer ${patientsAffiches.length} patient(s) ?`}
          libelleConfirmer="Oui, tout supprimer"
          motAConfirmer="SUPPRIMER"
          enCours={toutSuppressionEnCours}
          onConfirmer={confirmerSuppressionTous}
          onAnnuler={() => setToutSupprimer(false)}
        >
          <p>
            Vous allez supprimer <strong>{patientsAffiches.length}</strong> patient(s) de la vue{' '}
            <strong>« {portee === 'session' ? 'Cette session' : 'Tout le mois'} »</strong>.
          </p>
          {portee === 'mois' && (
            <p>
              Toutes les consultations du mois seront effacées, y compris celles encore <strong>en attente d'envoi</strong> sur cet appareil.
              Le CPN et les autres modules ne sont pas concernés.
            </p>
          )}
          <p>
            <strong>Cette action est définitive.</strong> Les patients seront retirés de la saisie, du RMA et des exports.
          </p>
        </ConfirmDialog>
      )}
    </div>
  );
}
